"use client";

import { useEffect, useId, useRef, useState } from "react";
const defaultMeetingUrl = process.env.NEXT_PUBLIC_HUBSPOT_MEETING_URL ?? "";

type HubspotMeetingEmbedProps = {
  meetingUrl?: string;
};

declare global {
  interface Window {
    MeetingsEmbedCode?: {
      createMeetingsIframe: (selector: string) => void;
    };
  }
}

const HUBSPOT_SCRIPT_ID = "hubspot-meetings-widget-loader";

export function HubspotMeetingEmbed({ meetingUrl }: HubspotMeetingEmbedProps) {
  const reactId = useId();
  const targetId = `hubspot-meeting-${reactId.replace(/:/g, "")}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const url = meetingUrl ?? defaultMeetingUrl;

  useEffect(() => {
    if (!url) return;

    const container = containerRef.current;
    if (!container) return;

    const embedSrc = url.includes("?") ? `${url}&embed=true` : `${url}?embed=true`;
    container.setAttribute("data-src", embedSrc);

    const mountWidget = () => {
      if (window.MeetingsEmbedCode) {
        window.MeetingsEmbedCode.createMeetingsIframe(`#${targetId}`);
        setIsReady(true);
      }
    };

    const existingScript = document.getElementById(HUBSPOT_SCRIPT_ID);
    if (existingScript) {
      if (window.MeetingsEmbedCode) {
        mountWidget();
      } else {
        existingScript.addEventListener("load", mountWidget);
        return () => existingScript.removeEventListener("load", mountWidget);
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    script.id = HUBSPOT_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.addEventListener("load", mountWidget);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", mountWidget);
    };
  }, [targetId, url]);

  if (!url) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-6 text-sm text-muted-foreground">
        Add `NEXT_PUBLIC_HUBSPOT_MEETING_URL` to enable the scheduling embed.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      id={targetId}
      className="meetings-iframe-container min-h-[600px] w-full overflow-hidden rounded-lg border border-border/60 bg-card"
      data-widget-ready={isReady}
    />
  );
}
